import { z } from 'zod';
import { observedWanIpv6Schema, wanIpv6Schema } from './wan-ipv6.js';

/**
 * Logical fields the UI cares about, each resolved to a concrete TR-069 path per
 * device profile. Ported from the legacy per-device mapping store, but keyed by
 * product class so one discovery serves every device of that model.
 */
export const PARAM_FIELDS = [
  'wan_path',
  'lan_path',
  'wifi_ssid',
  'wifi_key',
  'pppoe_user',
  'pppoe_pass',
  'hosts',
  'vlan_id',
] as const;
export const paramFieldSchema = z.enum(PARAM_FIELDS);
export type ParamField = z.infer<typeof paramFieldSchema>;

export const PARAM_FIELD_LABELS: Record<ParamField, string> = {
  wan_path: 'WAN connection',
  lan_path: 'LAN interface',
  wifi_ssid: 'WiFi SSID',
  wifi_key: 'WiFi passphrase',
  pppoe_user: 'PPPoE username',
  pppoe_pass: 'PPPoE password',
  hosts: 'Connected hosts table',
  vlan_id: 'WAN VLAN',
};

/** TR-069 data model root a device speaks. */
export const dataModelSchema = z.enum(['tr098', 'tr181', 'unknown']);
export type DataModel = z.infer<typeof dataModelSchema>;

export const deviceStatusSchema = z.enum(['online', 'offline', 'never_seen']);
export type DeviceStatus = z.infer<typeof deviceStatusSchema>;

/** Row shape for the ONU table — kept narrow so the poller can diff it cheaply. */
export const deviceSummarySchema = z.object({
  id: z.string(), // GenieACS _id
  orgId: z.string().nullable(),
  orgSlug: z.string().nullable(),
  serial: z.string(),
  alias: z.string().nullable(),
  manufacturer: z.string().nullable(),
  model: z.string().nullable(),
  productClass: z.string().nullable(),
  softwareVersion: z.string().nullable(),
  dataModel: dataModelSchema,
  lastInform: z.number().int().nullable(),
  status: deviceStatusSchema,
  wanIp: z.string().nullable(),
  mgmtIp: z.string().nullable(),
  ssid: z.string().nullable(),
  siteId: z.string().nullable(),
  tags: z.array(z.string()),
  /** Optical readings from the OLT, present once an OLT sweep has linked this unit. */
  rxPowerDbm: z.number().nullable(),
  oltRxPowerDbm: z.number().nullable(),
  ponPort: z.string().nullable(),
  onuIndex: z.string().nullable(),
});
export type DeviceSummary = z.infer<typeof deviceSummarySchema>;

/** Normalised WiFi auth, derived from BeaconType (TR-098) or ModeEnabled (TR-181). */
export const wlanSecuritySchema = z.enum(['open', 'wep', 'wpa', 'wpa2', 'wpa3', 'mixed', 'unknown']);
export type WlanSecurity = z.infer<typeof wlanSecuritySchema>;

export const WLAN_SECURITY_LABELS: Record<WlanSecurity, string> = {
  open: 'Open',
  wep: 'WEP',
  wpa: 'WPA',
  wpa2: 'WPA2',
  wpa3: 'WPA3',
  mixed: 'WPA/WPA2',
  unknown: '—',
};

export const wlanSchema = z.object({
  instance: z.string(),
  band: z.enum(['2.4G', '5G', 'unknown']),
  label: z.string(),
  enabled: z.boolean().nullable(),
  ssid: z.string().nullable(),
  passphrase: z.string().nullable(),
  security: wlanSecuritySchema,
  ssidPath: z.string().nullable(),
  passphrasePath: z.string().nullable(),
  enablePath: z.string().nullable(),
  /** Where the auth mode lives — TR-098 BeaconType, TR-181 Security.ModeEnabled. */
  securityPath: z.string().nullable(),
  /**
   * The auth mode EXACTLY as the CPE spells it, beside the normalised
   * `security` above.
   *
   * The normalised one answers "is this network open"; this one answers "what
   * word does this device use for that", and the two vendors do not agree: a
   * ZTE F670L says `None` and refuses `Basic` outright (cwmp.9003, live
   * 2026-09-24), while a Huawei HG8546M takes `Basic` with its Basic sub-modes
   * (live 2026-09-09). A write has to be in the device's own vocabulary, so
   * the device's own vocabulary has to reach the writer.
   */
  securityRaw: z.string().nullable().optional(),
  /**
   * The WAN connection the CPE ITSELF says this radio is bound to, where the
   * vendor exposes the binding — Huawei's `X_HW_LANBIND.SSID<n>Enable`.
   *
   * The ONU page used to answer "how does this SSID join the network" from the
   * `wifi_0/<n>` row in `onu_ports` alone, and a hotspot applied to a Huawei
   * ONT writes no such row: 82 units on gachie1 carry "Mymanga Public WiFi"
   * bridged onto VLAN 12 on the hardware and every one of them read "LAN" on
   * the card (operator, 2026-09-23). The CPE knows; this is the CPE's answer,
   * and the port row still wins where there is one.
   */
  network: z
    .object({
      name: z.string().nullable(),
      vlanId: z.number().int().nullable(),
      bridged: z.boolean(),
    })
    .nullable()
    .optional(),
});
export type Wlan = z.infer<typeof wlanSchema>;

export const hostSchema = z.object({
  hostname: z.string().nullable(),
  ip: z.string().nullable(),
  mac: z.string().nullable(),
  iface: z.string().nullable(),
  active: z.boolean(),
});
export type ConnectedHost = z.infer<typeof hostSchema>;

/**
 * Where each editable field lives on this device. The API resolves these
 * because the answer depends on the data model and the vendor's layout; the
 * client only ever sends back a path and a value.
 *
 * A null path means the device does not expose that field, and the UI should
 * show it read-only rather than offering an edit that would fail.
 */
export const writablePathsSchema = z.record(z.string(), z.string().nullable());
export type WritablePaths = Record<string, string | null>;

/**
 * How the WAN gets its address. DHCP and Static are two settings of one IP
 * connection; PPPoE is a different connection object entirely, which is why
 * switching to or from it means selecting another WAN instance rather than
 * flipping a field.
 */
export const wanModeSchema = z.enum(['dhcp', 'static', 'pppoe', 'unknown']);
export type WanMode = z.infer<typeof wanModeSchema>;

/** One WAN connection the device exposes, so the operator can pick between them. */
export const wanCandidateSchema = z.object({
  path: z.string(),
  kind: z.enum(['ppp', 'ip']),
  label: z.string(),
  mode: wanModeSchema,
  connected: z.boolean(),
  externalIp: z.string().nullable(),
  /**
   * The VLAN this connection rides, as the CPE reports it. The WAN VLAN picker
   * offers these alongside the VLANs attached here: an ONU provisioned by
   * whatever this platform replaced carries VLANs SimpleACS has no record of,
   * and an empty picker is a worse answer than the truth off the device.
   */
  vlanId: z.string().nullable(),
  /** It carries this platform's management traffic, so it is not a choice. */
  management: z.boolean(),
  /**
   * What the connection is FOR, read off the CPE — its service list and its
   * connection type — never off which one has an address (2026-10-05).
   * `bridge` is a connection the router passes straight through (a hotspot
   * VLAN's `WIFI_Bridge_VLAN_12`); it reaches the outside world too, which
   * is why the WAN card now lists every connection rather than one.
   */
  role: z.enum(['internet', 'bridge', 'management', 'voip', 'iptv', 'other']),
  /** The CPE's own word: IP_Routed, IP_Bridged, PPPoE_Routed … */
  connectionType: z.string().nullable(),
  uptimeSeconds: z.number().int().nullable(),
  /** The LAN sockets and SSIDs the router binds to this connection: "LAN 1", "WiFi 2". */
  serves: z.array(z.string()),
  /** The subscriber WAN — the one the WAN card's fields edit. */
  primary: z.boolean(),
  /*
    The rest of what a connection's own card shows (operator, 2026-10-05:
    "split the wans into different cards, not share one"). Optional: a
    document that has not read the leaf says nothing rather than a guess.
  */
  subnetMask: z.string().nullable().optional(),
  /** DefaultGateway on an IP connection, the PPP peer (RemoteIPAddress) on a PPPoE one. */
  gateway: z.string().nullable().optional(),
  dns: z.string().nullable().optional(),
  macAddress: z.string().nullable().optional(),
  natEnabled: z.boolean().nullable().optional(),
});
export type WanCandidate = z.infer<typeof wanCandidateSchema>;

export const wanInfoSchema = z.object({
  path: z.string().nullable(),
  kind: z.enum(['ppp', 'ip', 'unknown']),
  mode: wanModeSchema,
  connectionType: z.string().nullable(),
  externalIp: z.string().nullable(),
  subnetMask: z.string().nullable(),
  gateway: z.string().nullable(),
  dns: z.string().nullable(),
  /**
   * The WAN MAC. Networks that authenticate subscribers by MAC need this, and
   * it is not otherwise visible once the ONU is installed.
   */
  macAddress: z.string().nullable(),
  vlanId: z.string().nullable(),
  uptimeSeconds: z.number().int().nullable(),
  pppoeUsername: z.string().nullable(),
  pppoePassword: z.string().nullable(),
  /**
   * Whether SimpleACS holds the PPPoE password for this WAN at all — written
   * through the platform (its credential store) or recorded on the linked
   * ONU's VLAN row. False on a working PPPoE means the password was applied
   * OUTSIDE SimpleACS (before the platform, or on the router's own page): the
   * WAN card says so and a Resync leaves the connection alone (operator,
   * 2026-10-21). Never the password itself.
   */
  pppoePasswordHeld: z.boolean(),
  /**
   * Keys: addressingType, externalIp, subnetMask, gateway, dns,
   * pppoeUsername, pppoePassword, vlanId, connectionType, enable.
   */
  writable: writablePathsSchema,
  /** Every WAN connection instance on the device. */
  candidates: z.array(wanCandidateSchema),
  /**
   * The IPv6 half of the scored subscriber WAN as the CPE REPORTS it — the
   * dialect decided by the leaves the connection carries, the protocol and
   * modes read off the vendor words, the vendor's own connection status and
   * link-local. Null when there is no subscriber WAN. This is the truth the
   * pills show beside the record, because a "dual-stack" record can be applied
   * perfectly and deliver nothing until the upstream offers RA/DHCPv6.
   */
  ipv6: observedWanIpv6Schema.nullable(),
});
export type WanInfo = z.infer<typeof wanInfoSchema>;

export const lanInfoSchema = z.object({
  path: z.string().nullable(),
  ip: z.string().nullable(),
  subnetMask: z.string().nullable(),
  dhcpEnabled: z.boolean().nullable(),
  dhcpStart: z.string().nullable(),
  dhcpEnd: z.string().nullable(),
  dhcpLeaseTime: z.number().int().nullable(),
  /**
   * The DNS servers the ONU hands its own clients, as the CPE reports them —
   * one field, comma- or space-separated. Both vendors keep it here rather than
   * per-server: a ZTE F670L and a Huawei HG8546M both answer on
   * `LANHostConfigManagement.DNSServers`.
   */
  dns: z.string().nullable(),
  /** Keys: ip, subnetMask, dhcpEnabled, dhcpStart, dhcpEnd, dhcpLeaseTime, dns. */
  writable: writablePathsSchema,
});
export type LanInfo = z.infer<typeof lanInfoSchema>;

/**
 * The connection this platform reaches the CPE on, told apart from the WAN.
 *
 * Every CPE in this fleet carries two: one for the subscriber and one for
 * management, and the CPE itself says which is which. Reporting only "the WAN"
 * meant the page had to guess, and it guessed the management one — a Huawei
 * showed 10.10.1.156, this platform's own address, as the customer's WAN IP.
 */
export const managementInfoSchema = z.object({
  path: z.string().nullable(),
  ip: z.string().nullable(),
  vlanId: z.string().nullable(),
});
export type ManagementInfo = z.infer<typeof managementInfoSchema>;

/**
 * One subscriber port-forward as the CPE reports it — a TR-098 `PortMapping`
 * entry. The `index` is the instance number under the WAN connection's
 * `PortMapping` table, which is what a delete addresses.
 */
export const portForwardSchema = z.object({
  index: z.number().int(),
  description: z.string().nullable(),
  protocol: z.string().nullable(),
  externalPort: z.number().int().nullable(),
  internalPort: z.number().int().nullable(),
  internalClient: z.string().nullable(),
  enabled: z.boolean().nullable(),
  /** Source host the forward is restricted to; null/empty means any WAN source. */
  remoteHost: z.string().nullable(),
});
export type PortForward = z.infer<typeof portForwardSchema>;

/**
 * The port-forwarding table plus what this model can do with it: `supported`
 * whether a PortMapping table exists at all, `remoteHostSupported` whether the
 * model exposes the RemoteHost leaf (a ZTE F670L does not — its entry carries
 * only six leaves), so the UI only offers source-host scoping where it works.
 */
export const portForwardStateSchema = z.object({
  supported: z.boolean(),
  remoteHostSupported: z.boolean(),
  entries: z.array(portForwardSchema),
});
export type PortForwardState = z.infer<typeof portForwardStateSchema>;

/**
 * One CPE security control the model actually exposes over TR-069 — a firewall
 * level, a per-service WAN/LAN access toggle, an ICMP-reply switch, a service
 * enable, or an account field. The platform resolves each `key` to the vendor's
 * own parameter (Huawei `X_HW_Security.*`, ZTE `Firewall.*`/`X_ZTE-COM_*`) and
 * returns ONLY the controls whose parameter is present, so nothing unsupported
 * is ever offered.
 */
export const securityControlKindSchema = z.enum(['toggle', 'select', 'text', 'password']);
export type SecurityControlKind = z.infer<typeof securityControlKindSchema>;

export const securityControlSchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: securityControlKindSchema,
  /** Current value: boolean for a toggle, string for select/text; null when unknown or write-only. */
  value: z.union([z.boolean(), z.string()]).nullable(),
  /** For a select — the values the model accepts. */
  options: z.array(z.string()).optional(),
  writable: z.boolean(),
  /** A password/secret: never read back, only written. */
  secret: z.boolean().optional(),
  /** A short hint under the control. */
  note: z.string().optional(),
});
export type SecurityControl = z.infer<typeof securityControlSchema>;

export const securityGroupSchema = z.object({
  key: z.string(),
  label: z.string(),
  controls: z.array(securityControlSchema),
});
export type SecurityGroup = z.infer<typeof securityGroupSchema>;

export const securityStateSchema = z.object({
  /** Whether the CPE exposes any security control at all (a device on TR-069). */
  supported: z.boolean(),
  groups: z.array(securityGroupSchema),
});
export type SecurityState = z.infer<typeof securityStateSchema>;

/** Writing a set of security controls back — keyed by the control `key`. */
export const setSecuritySchema = z.object({
  values: z
    .array(z.object({ key: z.string().min(1), value: z.union([z.boolean(), z.string()]) }))
    .min(1),
});
export type SetSecurityRequest = z.infer<typeof setSecuritySchema>;

/**
 * A generic model-aware config section (Management, Network, Time, WiFi
 * advanced) shares the security shapes: a section is groups of controls the
 * model actually exposes, and writing one is the same keyed set. Re-exported
 * under section-neutral names so `/config/:section` reads naturally.
 */
export const configSectionStateSchema = securityStateSchema;
export type ConfigSectionState = z.infer<typeof configSectionStateSchema>;
export const setConfigSectionSchema = setSecuritySchema;
export type SetConfigSectionRequest = z.infer<typeof setConfigSectionSchema>;

/**
 * On-demand ONU diagnostics over TR-069 — an IP ping and a traceroute the CPE
 * runs against a host and reports back. The platform sets the target and flips
 * `DiagnosticsState` to `Requested`; the CPE runs it and the state settles to
 * `Complete` or an `Error_*`. Read-back is a projection of the standard
 * `IPPingDiagnostics` / `TraceRouteDiagnostics` objects, so both fleet vendors
 * carry it with no per-vendor branch.
 */
export const pingResultSchema = z.object({
  /** DiagnosticsState: None / Requested / Complete / Error_* — the run's lifecycle. */
  state: z.string().nullable(),
  host: z.string().nullable(),
  successCount: z.number().int().nullable(),
  failureCount: z.number().int().nullable(),
  averageResponseMs: z.number().int().nullable(),
  minResponseMs: z.number().int().nullable(),
  maxResponseMs: z.number().int().nullable(),
});
export type PingResult = z.infer<typeof pingResultSchema>;

export const traceRouteHopSchema = z.object({
  host: z.string().nullable(),
  hostAddress: z.string().nullable(),
});
export const traceRouteResultSchema = z.object({
  state: z.string().nullable(),
  host: z.string().nullable(),
  responseTimeMs: z.number().int().nullable(),
  hops: z.array(traceRouteHopSchema),
});
export type TraceRouteResult = z.infer<typeof traceRouteResultSchema>;

export const diagnosticsStateSchema = z.object({
  pingSupported: z.boolean(),
  traceRouteSupported: z.boolean(),
  ping: pingResultSchema.nullable(),
  traceRoute: traceRouteResultSchema.nullable(),
});
export type DiagnosticsState = z.infer<typeof diagnosticsStateSchema>;

export const runDiagnosticSchema = z.object({
  type: z.enum(['ping', 'traceroute']),
  host: z.string().min(1).max(255),
  count: z.number().int().min(1).max(64).optional(),
});
export type RunDiagnosticRequest = z.infer<typeof runDiagnosticSchema>;

/**
 * VoIP (SIP) status as the CPE reports it — read-mostly. Each voice line's
 * registration and call state, plus the profile's SIP servers, from the standard
 * `Services.VoiceService.{i}.VoiceProfile.{i}` tree. Surfaced so an operator can
 * see whether a subscriber's phone line registered without opening the CPE UI.
 */
export const voipLineSchema = z.object({
  index: z.number().int(),
  enabled: z.boolean().nullable(),
  directoryNumber: z.string().nullable(),
  authUserName: z.string().nullable(),
  uri: z.string().nullable(),
  status: z.string().nullable(),
  callState: z.string().nullable(),
});
export type VoipLine = z.infer<typeof voipLineSchema>;

export const voipProfileSchema = z.object({
  index: z.number().int(),
  proxyServer: z.string().nullable(),
  proxyServerPort: z.number().int().nullable(),
  registrarServer: z.string().nullable(),
  userAgentDomain: z.string().nullable(),
  lines: z.array(voipLineSchema),
});
export type VoipProfile = z.infer<typeof voipProfileSchema>;

export const voipStateSchema = z.object({
  supported: z.boolean(),
  profiles: z.array(voipProfileSchema),
});
export type VoipState = z.infer<typeof voipStateSchema>;

export const deviceDetailSchema = deviceSummarySchema.extend({
  hardwareVersion: z.string().nullable(),
  zoneId: z.string().nullable(),
  splitterId: z.string().nullable(),
  uptimeSeconds: z.number().int().nullable(),
  connectionRequestUrl: z.string().nullable(),
  /** Firewall as the CPE reports it over TR-069; null when the state is unknown. */
  firewall: z.boolean().nullable(),
  /** Whether the CPE exposes a firewall control over TR-069 at all. */
  firewallSupported: z.boolean(),
  /** The subscriber port-forwarding table, and whether the model has one. */
  portForward: portForwardStateSchema,
  wan: wanInfoSchema,
  /** Where this platform talks to the CPE — never the subscriber's WAN. */
  management: managementInfoSchema,
  lan: lanInfoSchema,
  wlans: z.array(wlanSchema),
  hosts: z.array(hostSchema),
  /** Effective path map (profile + per-device overrides) with its source. */
  paramMap: z.record(
    paramFieldSchema,
    z.object({ path: z.string(), source: z.enum(['profile', 'override', 'discovered']) }),
  ),
});
export type DeviceDetail = z.infer<typeof deviceDetailSchema>;

export const deviceListQuerySchema = z.object({
  search: z.string().trim().max(120).optional(),
  status: deviceStatusSchema.optional(),
  claim: z.enum(['claimed', 'unclaimed', 'any']).default('any'),
  siteId: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  sort: z.enum(['lastInform', 'serial', 'alias', 'model']).default('lastInform'),
  dir: z.enum(['asc', 'desc']).default('desc'),
});
export type DeviceListQuery = z.infer<typeof deviceListQuerySchema>;

export const setParametersSchema = z.object({
  values: z
    .array(
      z.object({
        path: z.string().min(1).max(400),
        value: z.union([z.string(), z.number(), z.boolean()]),
        type: z.enum(['xsd:string', 'xsd:int', 'xsd:unsignedInt', 'xsd:boolean']).optional(),
      }),
    )
    .min(1)
    .max(64),
  /** Push immediately with a Connection Request instead of waiting for the next inform. */
  connectionRequest: z.boolean().default(true),
});
export type SetParametersRequest = z.infer<typeof setParametersSchema>;

export const deviceActionSchema = z.enum([
  'summon', // connection request + refresh of the common parameter set
  'refresh', // refreshObject on the full tree
  'reboot',
  'factoryReset',
]);
export type DeviceAction = z.infer<typeof deviceActionSchema>;

/**
 * What a queued task will actually do to the ONU.
 *
 * The list only ever showed a task's name — `setParameterValues`, over and
 * over — which is the least useful thing about it. The interesting part is
 * WHICH parameters, and a task stuck behind a fault is exactly when somebody
 * needs to see them.
 */
export const deviceTaskSchema = z.object({
  id: z.string(),
  name: z.string(),
  device: z.string(),
  timestamp: z.number().int().nullable(),
  /** The CWMP fault it last hit. Its `detail` is the CPE's own words. */
  fault: z
    .object({
      code: z.string().nullable(),
      message: z.string().nullable(),
      detail: z.string().nullable(),
    })
    .nullable(),
  retries: z.number().int().nullable(),
  /** `setParameterValues`: path, value, and the XSD type where one was given. */
  parameterValues: z.array(z.tuple([z.string(), z.string(), z.string().nullable()])).nullable(),
  /** `getParameterValues`: the paths being read. */
  parameterNames: z.array(z.string()).nullable(),
  /** `refreshObject` / `addObject` / `deleteObject`: the subtree involved. */
  objectName: z.string().nullable(),
});
export type DeviceTask = z.infer<typeof deviceTaskSchema>;

export const deviceFaultSchema = z.object({
  id: z.string(),
  channel: z.string(),
  code: z.string(),
  message: z.string(),
  retries: z.number().int(),
  timestamp: z.number().int().nullable(),
  /**
   * Everything the CPE said, pretty-printed.
   *
   * A CWMP fault message is a summary — "9003 Invalid arguments" — and the
   * detail underneath it names the parameter that was refused. The table can
   * only fit the summary, so the detail is what the modal is for.
   */
  detail: z.string().nullable(),
});
export type DeviceFault = z.infer<typeof deviceFaultSchema>;

/**
 * Something the router reports that deserves a look but is not a fault the ACS
 * raised — shown as a WARNING on the Tasks & faults tab, with Resync and Ignore
 * (operator, 2026-09-26: "add an alert for multiple routed wan on onus, more of
 * a warning than error"). One kind so far: more than one routed internet WAN —
 * the shape two gachie1 F670Ls lost their WAN to that day (the router's own
 * `Internet_DHCP` and an OLT-made `omci_ipv4_dhcp_1`, two DHCP clients on VLAN 5).
 */
export const deviceWarningConnectionSchema = z.object({
  path: z.string(),
  name: z.string().nullable(),
  vlanId: z.number().int().nullable(),
  /** `ConnectionStatus` as the router last reported it. */
  status: z.string().nullable(),
  /** Made by the OLT over OMCI (`omci_*`) rather than by the router itself. */
  oltMade: z.boolean(),
});
export type DeviceWarningConnection = z.infer<typeof deviceWarningConnectionSchema>;

export const deviceWarningKindSchema = z.enum(['multiple_routed_wan']);
export type DeviceWarningKind = z.infer<typeof deviceWarningKindSchema>;

export const deviceWarningSchema = z.object({
  kind: deviceWarningKindSchema,
  /**
   * The exact set the warning is about. Ignore records it, so an ignored warning
   * stays quiet only while the router reports that same set — a connection added
   * or gone raises it again.
   */
  fingerprint: z.string(),
  summary: z.string(),
  /** What to do about it, in the router's own terms. */
  hint: z.string(),
  connections: z.array(deviceWarningConnectionSchema),
});
export type DeviceWarning = z.infer<typeof deviceWarningSchema>;

export const ignoreDeviceWarningSchema = z.object({
  kind: deviceWarningKindSchema,
  fingerprint: z.string().min(1).max(1000),
});
export type IgnoreDeviceWarningInput = z.infer<typeof ignoreDeviceWarningSchema>;

export const configureWanSchema = z.object({
  mode: wanModeSchema.exclude(['unknown']),
  vlanId: z.string().max(8).optional(),
  username: z.string().max(120).optional(),
  password: z.string().max(200).optional(),
  staticIp: z.string().max(45).optional(),
  subnetMask: z.string().max(45).optional(),
  gateway: z.string().max(45).optional(),
  dns: z.string().max(120).optional(),
  /**
   * Whether the connection terminates the VLAN (`routed`: ConnectionType
   * IP_Routed, NAT on) or hands it to the LAN (`bridged`: IP_Bridged, NAT off).
   *
   * When given it is written onto the EXISTING connection too, wherever the
   * CPE reports the leaves writable. Absent, an existing connection keeps the
   * role it has — which is how a spare HG8546M that arrived with its VLAN-5
   * WAN as a bridge stayed a bridge through a "switch to DHCP" (gachie1,
   * 2026-09-09): addressing changed, the role did not, and the subscriber's
   * devices got no address.
   */
  connectionType: z.enum(['routed', 'bridged']).optional(),
  /** Delete the replaced connection rather than leaving it disabled. */
  removeExisting: z.boolean().default(false),
  /**
   * Delete the subscriber WAN and build it again from these settings, even
   * when they are the ones it already has (operator, 2026-10-07: "when you
   * apply settings even if they are identical with existing, delete and
   * reapply"). A DHCP → PPPoE change then REPLACES the connection rather than
   * standing a second one up beside it. Never touches the TR-069 connection.
   */
  rebuild: z.boolean().default(false),
  /**
   * The IPv6 half. Absent or null = NO IPv6 leaf is written (the rule every
   * existing caller relies on); `{ protocol: "ipv4" }` switches IPv6 off;
   * dual stack names the address and prefix modes. Written as SEPARATE tasks
   * after the IPv4 set, to the scored subscriber connection, only the leaves
   * that connection reports, in the dialect it speaks — see wan-ipv6.ts.
   */
  ipv6: wanIpv6Schema.nullable().optional(),
});
export type ConfigureWanRequest = z.infer<typeof configureWanSchema>;
export type WanConnectionType = NonNullable<ConfigureWanRequest['connectionType']>;

const IPV4_RE = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
/** A source-host restriction: a LAN/WAN IPv4, or '' meaning "any source". */
const remoteHostField = z.union([z.literal(''), z.string().regex(IPV4_RE, 'Enter a source IPv4 address, or leave blank for any')]);

/** Adding one subscriber port-forward on the CPE over TR-069. */
export const addPortForwardSchema = z.object({
  protocol: z.enum(['TCP', 'UDP']),
  externalPort: z.coerce.number().int().min(1).max(65535),
  internalPort: z.coerce.number().int().min(1).max(65535),
  internalClient: z.string().regex(IPV4_RE, 'Enter the LAN IPv4 address to forward to'),
  description: z.string().max(64).optional(),
  /** Restrict to one source host; omit or '' for any WAN source. */
  remoteHost: remoteHostField.optional(),
});
export type AddPortForwardRequest = z.infer<typeof addPortForwardSchema>;

/** Add an SSID: enable the next dormant WLAN slot on the band, else create one. */
export const addWlanSchema = z.object({
  band: z.enum(['2.4G', '5G', 'unknown']).optional(),
});
export type AddWlanRequest = z.infer<typeof addWlanSchema>;

/**
 * One card's worth of a CPE, re-read on its own — the per-card refresh that
 * spares the operator a whole Summon. Each name is a card on the ONU page.
 */
export const deviceRefreshScopeSchema = z.enum([
  'wan',
  'lan',
  'wifi',
  'hosts',
  'device',
  'management',
  'network',
  'time',
  'wifi_advanced',
  'security',
  'voip',
]);
export type DeviceRefreshScope = z.infer<typeof deviceRefreshScopeSchema>;
export const deviceRefreshSchema = z.object({ scope: deviceRefreshScopeSchema });

/**
 * Editing one existing forward — every field optional, at least one required.
 * `enabled` doubles as the per-entry on/off toggle (enable/disable without
 * removing the entry), so a single PATCH covers edit and toggle alike.
 */
export const editPortForwardSchema = z
  .object({
    protocol: z.enum(['TCP', 'UDP']).optional(),
    externalPort: z.coerce.number().int().min(1).max(65535).optional(),
    internalPort: z.coerce.number().int().min(1).max(65535).optional(),
    internalClient: z.string().regex(IPV4_RE, 'Enter the LAN IPv4 address to forward to').optional(),
    description: z.string().max(64).optional(),
    enabled: z.boolean().optional(),
    remoteHost: remoteHostField.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Change at least one field',
  });
export type EditPortForwardRequest = z.infer<typeof editPortForwardSchema>;

export const claimRequestSchema = z.object({
  orgId: z.string().nullable(),
  alias: z.string().max(120).optional(),
});
export type ClaimRequest = z.infer<typeof claimRequestSchema>;
